import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MessageCircle, Wand2 } from "lucide-react";
import { Reveal, MaskLineInView } from "./motion/Reveal";
import { CATEGORIES } from "../data/content";
import { WHATSAPP_URL } from "../lib/site";
import { useLowestPrice } from "../lib/useApi";
import { formatPrice } from "../lib/filters";
import { HelpMeChoose } from "./HelpMeChoose";

const CategoryCard = ({ cat, delay, fromPrice }) => {
  const points = [
    ...cat.points.slice(0, 2),
    ...(Number.isFinite(fromPrice) ? [`From ${formatPrice(fromPrice)}`] : []),
  ];
  return (
  <Reveal delay={delay} y={40} className="group">
    <Link
      to={`/vanities?mount=${cat.id}`}
      data-testid={`category-card-${cat.id}`}
      className="block relative overflow-hidden rounded-sm bg-charcoal"
    >
      <div className="relative aspect-[3/4] md:aspect-[4/5] overflow-hidden">
        <img
          src={cat.image}
          alt={`${cat.title} vanities`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent" />

        <span className="absolute top-6 left-6 font-serif text-ivory/70 text-lg">
          {cat.index}
        </span>

        <div className="absolute inset-x-0 bottom-0 p-6 md:p-9 text-ivory">
          <p className="eyebrow text-ivory/60 mb-2">{cat.subtitle}</p>
          <h3 className="font-serif font-light text-4xl md:text-5xl leading-none">
            {cat.title}
          </h3>
          <p className="mt-4 max-w-sm text-ivory/75 text-sm leading-relaxed">
            {cat.copy}
          </p>

          <ul className="mt-5 space-y-2">
            {points.map((pt) => (
              <li key={pt} className="flex items-center gap-2.5 text-ivory/85 text-xs md:text-sm">
                <span className="grid place-items-center h-4 w-4 rounded-full border border-ivory/40 shrink-0">
                  <Check size={10} strokeWidth={2} />
                </span>
                {pt}
              </li>
            ))}
          </ul>

          <div className="mt-7 inline-flex items-center gap-3 text-sm font-medium">
            <span className="relative">
              {cat.cta}
              <span className="absolute left-0 -bottom-1 h-px w-full origin-left scale-x-100 bg-ivory/60 transition-transform duration-500 group-hover:scale-x-0" />
            </span>
            <span className="grid place-items-center h-9 w-9 rounded-full border border-ivory/40 transition-colors duration-500 group-hover:bg-ivory group-hover:text-charcoal">
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  </Reveal>
  );
};

export const Categories = () => {
  const wall = useLowestPrice("Wall Mounted");
  const floor = useLowestPrice("Floor Mounted");
  const prices = { wall: wall.data, floor: floor.data };

  return (
  <section
    id="categories"
    data-testid="categories-section"
    className="py-16 md:py-32 bg-ivory"
  >
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 md:mb-20">
        <div>
          <p className="eyebrow text-taupe mb-4">Shop by Category</p>
          <h2 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-2xl">
            <MaskLineInView>Two collections,</MaskLineInView>
            <MaskLineInView delay={0.1} className="italic text-taupe">
              built around your bathroom.
            </MaskLineInView>
          </h2>
        </div>
        <p className="max-w-xs text-charcoal/65 text-sm leading-relaxed md:text-right">
          Everything BathLane makes lives in one of two places. Pick the form
          that fits your space — we'll help with the rest.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {CATEGORIES.map((cat, i) => (
          <CategoryCard key={cat.id} cat={cat} delay={i * 0.12} fromPrice={prices[cat.id]} />
        ))}
      </div>

      {/* Help-me-choose cue */}
      <div
        data-testid="help-me-choose"
        className="mt-6 md:mt-12 flex flex-col items-center gap-4 text-center"
      >
        <p className="text-sm text-charcoal/70">Not sure which one fits your bathroom?</p>
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <HelpMeChoose
            trigger={
              <button
                data-testid="help-me-choose-quiz"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500 w-full sm:w-auto"
              >
                <Wand2 size={16} strokeWidth={1.6} />
                Help me choose
              </button>
            }
          />
          <a
            href={WHATSAPP_URL("Hi BathLane, help me choose between wall mounted and floor mounted.")}
            target="_blank"
            rel="noreferrer"
            data-testid="help-me-choose-whatsapp"
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-charcoal link-sweep"
          >
            <MessageCircle size={15} strokeWidth={1.5} />
            Ask on WhatsApp
          </a>
        </div>
      </div>
    </div>
  </section>
  );
};
